import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import {
  isUnparsedJsonBody,
  legalAcceptanceRequiredFromResponse,
  MalformedResponse,
  subscriptionRequiredFromResponse,
} from "@/api/errors";
import { runtimeEnv } from "@/lib/runtimeEnv";
import { isClerkNetworkError } from "@fomofi/shared";

const API_BASE_URL = runtimeEnv.apiBaseUrl();

if (!API_BASE_URL) {
  throw new Error(
    "Missing API base URL: set APP_API_BASE_URL on the container or VITE_API_BASE_URL in .env",
  );
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

let getTokenFn: (() => Promise<string | null>) | null = null;
type SessionExpiredHandler = (returnTo: string) => void;
type BrowserLocation = Pick<Location, "pathname" | "search" | "hash">;

let sessionExpiredHandler: SessionExpiredHandler | null = null;

export function setAuthTokenProvider(fn: () => Promise<string | null>) {
  getTokenFn = fn;
}

export function setSessionExpiredHandler(handler: SessionExpiredHandler | null) {
  sessionExpiredHandler = handler;
}

/**
 * CALL-04 (2026-10-26): what to do when any API answers 402 (subscription required).
 * The App registers a handler that sends the browser to Pick your plan; the response is
 * still rejected as `SubscriptionRequired` so callers behave as before.
 */
type SubscriptionRequiredHandler = (location: BrowserLocation) => void;
let subscriptionRequiredHandler: SubscriptionRequiredHandler | null = null;

export function setSubscriptionRequiredHandler(handler: SubscriptionRequiredHandler | null) {
  subscriptionRequiredHandler = handler;
}

export function routeSubscriptionRequiredResponse(
  status: number | undefined,
  data: unknown,
  location: BrowserLocation = window.location,
): boolean {
  if (!subscriptionRequiredFromResponse(status, data) || !subscriptionRequiredHandler) return false;
  subscriptionRequiredHandler({
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  });
  return true;
}

export function routeExpiredSessionResponse(
  status: number | undefined,
  data: unknown,
  location: BrowserLocation = window.location,
): boolean {
  const body =
    typeof data === "object" && data !== null
      ? (data as { code?: unknown; detail?: unknown })
      : undefined;
  const isExpiredSession =
    body?.code === "session_expired" || body?.detail === "Invalid or expired token.";

  if ((status !== 401 && status !== 403) || !isExpiredSession || !sessionExpiredHandler) {
    return false;
  }

  sessionExpiredHandler(`${location.pathname}${location.search}${location.hash}`);
  return true;
}

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (getTokenFn) {
    let token: string | null;
    try {
      token = await getTokenFn();
    } catch (err) {
      // FP-174: with rethrowOfflineNetworkErrors on, a token refresh that can't reach
      // Clerk throws. Report it the way axios reports any request that got no answer
      // (ERR_NETWORK, which queryFailure reads as "offline") WITHOUT sending: sent with
      // no token, an online-but-Clerk-blocked request would 401 and sign the user out.
      if (isClerkNetworkError(err)) {
        throw new AxiosError("Network Error", AxiosError.ERR_NETWORK, config);
      }
      throw err;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => {
    // A body that claimed to be JSON and did not parse is a failure, not data.
    // axios leaves it as a raw string on `response.data` and resolves happily,
    // so without this every caller reads properties off a string and the page
    // renders as though it had an empty answer (BEN-141 QF4, `state.malformed`).
    if (isUnparsedJsonBody(response.headers?.["content-type"], response.data)) {
      return Promise.reject(new MalformedResponse(response.config?.url));
    }
    return response;
  },
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      routeExpiredSessionResponse(error.response?.status, error.response?.data);
      routeSubscriptionRequiredResponse(error.response?.status, error.response?.data);

      const subscriptionError = subscriptionRequiredFromResponse(
        error.response?.status,
        error.response?.data,
      );
      if (subscriptionError) {
        return Promise.reject(subscriptionError);
      }
      const legalAcceptanceError = legalAcceptanceRequiredFromResponse(
        error.response?.status,
        error.response?.data,
      );
      if (legalAcceptanceError) {
        return Promise.reject(legalAcceptanceError);
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
